import React, { useEffect, useState } from 'react';
import { Share2, Copy, Check, Sparkles, UserPlus } from 'lucide-react';
import { Button } from './ui/button';
import { apiClient } from '../api';
import { toast } from 'sonner';

/**
 * Replaces the bland "no profiles found" message with a positive, action-oriented
 * empty state. Shows the user's referral link so they can invite friends to grow
 * their local scene. Critical for the early "empty room" phase of the app.
 */
const EmptyBrowseInvite = ({ hasFilters, onClearFilters }) => {
  const [refLink, setRefLink] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    // Reuse the existing referral system — every user has a link
    apiClient.get('/referrals/me')
      .then((r) => {
        if (r.data?.code) {
          const base = window.location.origin;
          setRefLink(`${base}/?ref=${r.data.code}`);
        }
      })
      .catch(() => {});
  }, []);

  const copy = async () => {
    if (!refLink) return;
    try {
      await navigator.clipboard.writeText(refLink);
      setCopied(true);
      toast.success('Invite link copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Couldn\'t copy — long-press to copy manually');
    }
  };

  const share = async () => {
    if (!refLink) return;
    const shareData = {
      title: 'Join me on Flirty',
      text: 'Hey — checking out this new local dating app. Curious what you think 👀',
      url: refLink,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await copy();
      }
    } catch (err) { /* user cancelled */ }
  };

  // Different messaging based on whether filters narrowed results to zero
  if (hasFilters) {
    return (
      <div
        data-testid="empty-browse-filtered"
        className="max-w-md mx-auto mt-12 text-center px-6 py-10 rounded-2xl bg-fnl-purple/40 border border-fnl-purple-light/40"
      >
        <Sparkles className="w-12 h-12 text-fnl-pink/50 mx-auto mb-3" />
        <h3 className="text-xl font-bold text-white mb-2">No one matches those filters</h3>
        <p className="text-purple-300 text-sm mb-5">
          Try widening your age range or removing the location filter to see more people in your area.
        </p>
        <Button
          onClick={onClearFilters}
          className="bg-fnl-pink hover:bg-fnl-pink-light text-white font-semibold"
        >
          Clear all filters
        </Button>
      </div>
    );
  }

  // True empty state — invite-to-grow CTA
  return (
    <div
      data-testid="empty-browse-invite"
      className="max-w-xl mx-auto mt-10 text-center px-6 py-10 sm:px-10 sm:py-12 rounded-2xl bg-gradient-to-br from-fnl-pink/15 via-fnl-purple to-fnl-dark border border-fnl-pink/40 shadow-xl shadow-fnl-pink/10"
    >
      <div className="inline-flex items-center gap-2 px-3 py-1 mb-4 rounded-full bg-fnl-pink/20 border border-fnl-pink/50 text-fnl-pink text-[10px] font-bold uppercase tracking-wider">
        <Sparkles className="w-3 h-3" />Early Access
      </div>
      <h3 className="text-2xl sm:text-3xl font-bold text-white mb-3 leading-tight">
        You're early — let's fix that
      </h3>
      <p className="text-purple-200 mb-7 leading-relaxed">
        Flirty is just getting started in your area. Invite a few friends to grow the local scene —
        every friend who joins earns you a <b className="text-fnl-pink">free Boost credit</b> 💖
      </p>

      {refLink && (
        <div className="flex flex-col sm:flex-row gap-2 mb-6">
          <input
            readOnly
            value={refLink}
            onClick={(e) => e.target.select()}
            data-testid="empty-browse-invite-link"
            className="flex-1 bg-fnl-dark/60 border border-purple-800 rounded-lg px-3 py-2 text-purple-200 text-sm text-center sm:text-left font-mono truncate"
          />
          <Button
            onClick={copy}
            variant="outline"
            className="bg-fnl-purple-light/40 border-fnl-pink text-fnl-pink hover:bg-fnl-pink hover:text-white shrink-0"
            data-testid="empty-browse-copy-btn"
          >
            {copied ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
            {copied ? 'Copied' : 'Copy'}
          </Button>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-2">
        <Button
          onClick={share}
          className="bg-fnl-pink hover:bg-fnl-pink-light text-white font-semibold flex-1 gap-2"
          data-testid="empty-browse-share-btn"
        >
          <Share2 className="w-4 h-4" />Share Flirty
        </Button>
        <Button
          asChild
          variant="outline"
          className="bg-transparent border-purple-700 text-purple-100 hover:bg-fnl-purple-light/40 flex-1 gap-2"
        >
          <a href="/me">
            <UserPlus className="w-4 h-4" />Polish my profile
          </a>
        </Button>
      </div>

      <p className="text-xs text-purple-400 mt-5">
        Tip: profiles with photos + a bio get matched 5× faster
      </p>
    </div>
  );
};

export default EmptyBrowseInvite;
